import { useLeadActivities } from '@/hooks/useLeadActivities';
import { LeadDetailsModel } from '@/typings/models/Lead';
import { EVENT_TO_I18N_ID } from '@/typings/models/LeadActivity';
import { Empty, Spin, Timeline } from 'antd';
import classNames from 'classnames';
import { useMemo, useRef } from 'react';
import { useIntl } from 'umi';
import ActivityCard from './ActivityCard';
import {
  ActivityTone,
  getActivityPresentation,
  isEmptyExtraFieldChange,
} from './activityPresentation';
import styles from './index.less';
import { groupActivitiesByDay } from './utils';

export const TONE_CLASS: Record<ActivityTone, string> = {
  positive: styles.tonePositive,
  negative: styles.toneNegative,
  warning: styles.toneWarning,
  info: styles.toneInfo,
  accent: styles.toneAccent,
  neutral: styles.toneNeutral,
};

const LeadActivity = ({ leadDetails }: { leadDetails: LeadDetailsModel }) => {
  const intl = useIntl();
  const containerRef = useRef<HTMLDivElement>(null);

  const { activities, loading, paginationLoading } = useLeadActivities({
    leadId: leadDetails.id,
    containerRef,
  });

  const visibleActivities = useMemo(
    () =>
      activities.filter(
        (e) => EVENT_TO_I18N_ID[e.activity_type] && !isEmptyExtraFieldChange(e),
      ),
    [activities],
  );

  const groups = useMemo(
    () => groupActivitiesByDay(visibleActivities, intl),
    [visibleActivities, intl],
  );

  if (!loading && visibleActivities.length === 0) return <Empty />;

  return (
    <Spin spinning={loading} className={styles.activitySpin}>
      <div ref={containerRef} className={styles.leadActivityContainer}>
        {groups.map((group, groupIdx) => (
          <div
            key={`${group.key}-${groupIdx}`}
            className={styles.activityDateGroup}
          >
            {group.label && (
              <div className={styles.activityDateHeader}>{group.label}</div>
            )}
            <Timeline
              items={group.items.map((activity, idx) => {
                const { icon, tone } = getActivityPresentation(
                  activity.activity_type,
                );
                return {
                  key: activity.id ?? `${group.key}-${idx}`,
                  dot: (
                    <span
                      className={classNames(
                        styles.activityDot,
                        TONE_CLASS[tone],
                      )}
                    >
                      {icon}
                    </span>
                  ),
                  children: <ActivityCard activity={activity} />,
                };
              })}
            />
          </div>
        ))}
        {paginationLoading && (
          <div className={styles.activityPaginationLoader}>
            <Spin size="small" />
          </div>
        )}
      </div>
    </Spin>
  );
};

export default LeadActivity;
